import Router from 'vue-router'
import Vue from 'vue'

import store from '@/store'

import PageNotFoundView from '@/views/PageNotFound'

import EOIRCaseInfoPages from './eoir-case-info'
import adminPagesRoutes from './admin-pages'
import authenticationRoutes from './authentication'
import calendarRoutes from './calendar'
import dataPagesRoutes from './data-pages'
import engagementLettersRoutes from './public/engagement-letters'
import integrationPagesRoutes from './integration-pages'
import mainPagesRoutes from './main-pages'
import { notifyUserToReload } from './chunk-load-error-handler'
import settingsPages from './settings-pages'
import surveyPagesRoutes from './survey-pages'
import userAccountPages from './user-account-pages'

Vue.use(Router)

// Fallback shown whenever the destination route has no meta.title and its
// component doesn't set document.title itself (e.g. Case.vue, Record.vue).
// Without this, navigating to such a route left document.title unchanged,
// so the page kept showing whatever title the previous page had set.
const DEFAULT_TITLE = 'Innovation Law Lab Platform'

const router = new Router({
  mode: 'history',
  base: process.env.VUE_APP_PREFIX ? process.env.VUE_APP_PREFIX : '',
  routes: [
    ...adminPagesRoutes,
    ...authenticationRoutes,
    ...calendarRoutes,
    ...dataPagesRoutes,
    ...engagementLettersRoutes,
    ...EOIRCaseInfoPages,
    ...integrationPagesRoutes,
    ...mainPagesRoutes,
    ...surveyPagesRoutes,
    settingsPages,
    ...userAccountPages,
    { path: '*', meta: { title: 'Page Not Found - Innovation Law Lab Platform' }, component: PageNotFoundView },
  ],
})

router.onError(error => {
  if (error.name === 'ChunkLoadError') notifyUserToReload(router)
})

router.beforeEach((to, from, next) => {
  // Always reset the title on navigation (not just when meta.title is set) so a
  // stale title from the previous page never lingers on a route that doesn't
  // define one. Routes whose view sets document.title dynamically will overwrite
  // this default once their data loads.
  document.title = to.meta.title || DEFAULT_TITLE

  if (typeof to.matched[0]?.components.default === 'function') store.dispatch('router_progress/startLoading')
  if (!to.matched.some(record => record.meta.requiresAuth)) return next()

  if (store.getters.isLoggedIn) {
    // Assuming all admin-requiring routes also require auth, so okay to do nested
    // if must be admin, check current user
    if (to.matched.some(record => record.meta.requiresAdmin) && !store.state.currentUser.isAdmin) return next('/')
    return next()
  }

  // if page user is navigating to homepage, redirect directly to login
  // otherwise, get current path and add to url query for Login view to use
  if (to.fullPath === '/') next('/login')
  else {
    store.dispatch('handleSessionReturnTo', { returnTo: to.fullPath })
    next({ path: '/login' })
  }
})

// afterEach hooks do not get a next function and cannot affect the navigation
// it also has a 'from' arg: (to, from)
router.afterEach(to => {
  // store destination (except /login and reset_password) so it can be restored if user gets logged out
  // TODO: what other paths do we want to be sure not to store?
  if (
    to.fullPath !== '/login' &&
    !to.fullPath.includes('reset_password')
  ) {
    store.dispatch('handleSessionReturnTo', { returnTo: to.fullPath })
  }
  store.dispatch('router_progress/stopLoading')
})

export default router
